<template>
  <div class="px-[24px] h-screen flex flex-col justify-center">
    <div class="">
      <MainTitle>
        {{ $t('register_title') }}
      </MainTitle>

      <SocialAuth />

      <Divider class="my-5" />

      <SocialAuthUsed
        v-if="0"
        provider="Google"
        class="mt-4"
      />

      <EmailInput
        v-model="state.email"
        v-model:valid="state.emailIsValid"
        class="mt-4"
      />

      <PasswordInput
        v-model="state.password"
        v-model:valid="state.passwordIsValid"
      />

      <ErrorWidget
        v-if="userAlreadyExists"
        role="alert"
      >
        {{ $t('error_account_exist') }}
      </ErrorWidget>

      <ErrorWidget
        v-else-if="hasError"
        role="alert"
      >
        {{ $t('error_general') }}
      </ErrorWidget>

      <BaseButton
        class="mt-5"
        :loading="loading"
        :disabled="!canRegister"
        @click="registerAction"
      >
        {{ $t('continue') }}
      </BaseButton>

      <BottomLink
        class="mt-4"
        @click="goToLogin"
      >
        <template #title>
          {{ $t('have_account') }}
        </template>
        <template #link>
          {{ $t('login_link') }}
        </template>
      </BottomLink>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ROUTE_NAMES } from '@/router'
import SocialAuth from '@/components/buttons/social-auth/SocialAuth.vue'
import Divider from '@/components/ui/Divider.vue'
import SocialAuthUsed from '@/components/ui/SocialAuthUsed.vue'
import MainTitle from '@/components/ui/MainTitle.vue'
import PasswordInput from '@/components/inputs/PasswordInput.vue'
import BottomLink from '@/components/ui/BottomLink.vue'
import BaseButton from '@/components/buttons/BaseButton.vue'
import EmailInput from '@/components/inputs/EmailInput.vue'
import { registerUserViaEmail } from '@/services/api/Auth'
import { logAuthEvent } from '@/services/debug/authDebug'
import { useI18n } from 'vue-i18n'

import { redirectToAppUrlWithAuthorizationCode } from '@/services/auth/Auth.service'
import ErrorWidget from '@/components/ui/ErrorWidget.vue'

const router = useRouter()
const { locale } = useI18n()

const state = reactive({
  email: '',
  password: '',
  emailIsValid: false,
  passwordIsValid: false
})

const loading = ref(false)
const userAlreadyExists = ref(false)
const hasError = ref(false)

const canRegister = computed(() => !loading.value && state.emailIsValid && state.passwordIsValid)

const registerAction = async () => {
  if (!canRegister.value) return
  loading.value = true
  userAlreadyExists.value = false
  hasError.value = false
  logAuthEvent('auth.register.submit.started', {
    emailProvided: Boolean(state.email),
    passwordProvided: Boolean(state.password),
    language: locale.value
  })
  try {
    const result = await registerUserViaEmail({
      email: state.email,
      password: state.password,
      language: locale.value
    })
    const data = result?.data?.value
    const error = result?.error?.value
    const statusCode = result?.statusCode?.value

    if (data?.userAlreadyExists) {
      logAuthEvent(
        'auth.register.submit.user-exists',
        { hasAuthorizationCode: Boolean(data.authorizationCode) },
        'warn'
      )
      userAlreadyExists.value = true
      return
    }
    if (
      error ||
      statusCode >= 400 ||
      typeof data?.authorizationCode !== 'string' ||
      !data.authorizationCode.trim()
    ) {
      hasError.value = true
      logAuthEvent(
        'auth.register.submit.failed',
        {
          statusCode: statusCode ?? null,
          requestId: result?.response?.value?.headers?.get?.('x-correlation-id') || '',
          hasAuthorizationCode: Boolean(data?.authorizationCode),
          reason: error || statusCode >= 400 ? 'request-failed' : 'missing-authorization-code'
        },
        'error'
      )
      return
    }

    logAuthEvent('auth.register.submit.completed', { hasAuthorizationCode: true })
    await redirectToAppUrlWithAuthorizationCode(data.authorizationCode)
  } catch (error) {
    hasError.value = true
    logAuthEvent(
      'auth.register.submit.failed',
      { reason: 'unexpected-exception', errorName: error?.name || 'Error' },
      'error'
    )
  } finally {
    loading.value = false
  }
}

const goToLogin = () => router.push({ name: ROUTE_NAMES.LOGIN })
</script>
